import { createSlice, createEntityAdapter } from '@reduxjs/toolkit';

import { snakeToCamel } from '@shared/helpers';

import { deleteFile, fetchFiles, loadMoreFiles, renameFile, uploadFile } from './action';

const filesAdapter = createEntityAdapter({
    selectId: (payment) => payment.id,
});

const initialState = filesAdapter.getInitialState({
    fetchFilesLoading: false,
    deleteFileLoading: false,
    renameFileLoading: false,
    fetchFilesLoadingMore: false,
    needFetchFiles: true,
    needFetchMore: false,
    uploadFiles: [],
    error: null,
});

const filesSlice = createSlice({
    name: 'files',
    initialState,
    reducers: {
        addFiles: filesAdapter.addMany,
        updateOne: filesAdapter.updateOne,
        updateMany: filesAdapter.updateMany,
        changeUploadFiles: (state, { payload }) => {
            state.uploadFiles = payload;
        },
        changeNeedMore: (state, action) => {
            state.needFetchMore = action.payload;
        },
        changeNeedFiles: (state, action) => {
            state.needFetchFiles = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchFiles.pending, (state) => {
                state.fetchFilesLoading = true;
                state.error = null;
            })
            .addCase(fetchFiles.fulfilled, (state, action) => {
                state.fetchFilesLoading = false;
                const payloadAdapted = action.payload.files.map((file) => {
                    const arr = Object.entries(file).map(([k, v]) => [snakeToCamel(k), v]);
                    return Object.fromEntries(arr);
                });
                filesAdapter.addMany(state, payloadAdapted);
            })
            .addCase(fetchFiles.rejected, (state, action) => {
                state.fetchFilesLoading = false;
                state.error = action.error.message;
            })
            .addCase(deleteFile.pending, (state) => {
                state.deleteFileLoading = true;
                state.error = null;
            })
            .addCase(deleteFile.fulfilled, (state, action) => {
                state.deleteFileLoading = false;
                filesAdapter.removeOne(state, action.payload);
            })
            .addCase(deleteFile.rejected, (state, action) => {
                state.deleteFileLoading = false;
                state.error = action.error.message;
            })
            .addCase(renameFile.pending, (state) => {
                state.renameFileLoading = true;
                state.error = null;
            })
            .addCase(renameFile.fulfilled, (state, action) => {
                state.renameFileLoading = false;
                filesAdapter.updateOne(state, action.payload);
            })
            .addCase(renameFile.rejected, (state, action) => {
                state.renameFileLoading = false;
                state.error = action.error.message;
            })
            .addCase(uploadFile.fulfilled, (state, action) => {
                state.uploadFiles = [];
                const payloadAdapted = action.payload.map((file) => {
                    const arr = Object.entries(file).map(([k, v]) => [snakeToCamel(k), v]);
                    return Object.fromEntries(arr);
                });

                filesAdapter.addMany(state, payloadAdapted);

                const newIds = action.payload.map((file) => file.id);
                state.ids = [...newIds, ...state.ids.filter((id) => !newIds.includes(id))];
            })
            .addCase(uploadFile.rejected, (state, action) => {
                state.uploadFiles = [];
                state.error = action.error.message;
            })
            .addCase(loadMoreFiles.pending, (state) => {
                state.fetchFilesLoadingMore = true;
                state.error = null;
            })
            .addCase(loadMoreFiles.fulfilled, (state, action) => {
                state.fetchFilesLoadingMore = false;
                filesAdapter.addMany(state, action.payload);
            })
            .addCase(loadMoreFiles.rejected, (state, action) => {
                state.fetchFilesLoadingMore = false;
                state.error = action.error.message;
            });
    },
});

export const filesSelector = filesAdapter.getSelectors((state) => state.filesReducer);
export const { actions: filesActions } = filesSlice;
export const { reducer: filesReducer } = filesSlice;
